// src/components/GuestFormView.jsx — LOT E V4 (aperçu voyageur fidèle)
//
// Rendu PRÉSENTATIONNEL du formulaire voyageur, extrait tel quel de
// PublicFormPage.jsx (ADR-045/071/072/075/189, B5) pour être partagé entre :
//   1. la page publique voyageur (PublicFormPage — conteneur data : token,
//      /api/form-config, submitFormToken, upload pièces) ;
//   2. l'aperçu admin (FormulairesTab → FormLivePreview — mode preview inerte).
//
// Ce fichier ne fait AUCUN fetch, AUCUNE écriture, AUCUNE lecture de token :
// il reçoit un `formConfig` déjà résolu + l'état + des callbacks. Le conteneur
// décide de la source des données et du comportement de soumission/upload.
// Garantie zéro-régression voyageur : le corps du rendu est un MOVE byte-proche
// de PublicFormPage (mêmes styles, mêmes helpers, même charte gelée DSV).

import { useState, useRef, useLayoutEffect, useCallback } from "react"
import { DSV as DS } from "../lib/charte-voyageur"
import { resolveCoverPhoto } from "../lib/form-cover-photo"
import {
  t, FORM_LOCALES, FORM_LOCALE_LABELS,
  pieceOptionLabel, FORM_CONTACT_EMAIL, texteLimiteModification,
} from "../lib/form-i18n"

// Mapping ref champ pièce → clé de sous-texte chrome (i18n).
const PIECE_SUBTEXT_KEYS = {
  passport_photo: 'piece_subtext_passport',
  id_recto: 'piece_subtext_recto',
  id_verso: 'piece_subtext_verso',
  ts_recto: 'piece_subtext_ts_recto',
  ts_verso: 'piece_subtext_ts_verso',
}

// ADR-071 — Choix conditionnel pièce d'identité (hardcodé Option C).
// Le radio 'id_type' contrôle l'affichage de passport_photo VS id_recto+id_verso
// VS ts_recto+ts_verso (titre de séjour, décision 3 du 2026-09-06 — valeur
// canonique FR « Titre de séjour », JAMAIS traduite, piège id_type CLAUDE.md).
const ID_TYPE_REF = 'id_type'
const ID_TYPE_PASSPORT = 'Passeport'
const ID_TYPE_CI = "Carte d'identité"
const ID_TYPE_TS = 'Titre de séjour'
const PASSPORT_REFS = new Set(['passport_photo'])
const CI_REFS = new Set(['id_recto', 'id_verso'])
const TS_REFS = new Set(['ts_recto', 'ts_verso'])

// Décision produit 2026-09-06 — adresse de facturation retirée par canal
// (défaut : Airbnb). Référence canonique du champ visé, et lui seul.
const BILLING_ADDRESS_REF = 'billing_address'

// Point 6 — les champs de pièce d'identité (radio + uploads conditionnels)
// sont regroupés visuellement sous un titre de groupe. id_type ouvre le groupe.
export const PIECE_GROUP_REFS = new Set([ID_TYPE_REF, ...PASSPORT_REFS, ...CI_REFS, ...TS_REFS])

// ADR-189 amendement — quand la pièce d'identité n'est pas requise pour le
// (logement, canal), TOUT le module pièce (radio id_type + uploads) disparaît du
// formulaire et ne gate pas la soumission. `idDocRequired` vient de
// /api/form-config (défaut true si absent → rétrocompat).
// Décision produit 2026-09-06 — même mécanique pour l'adresse de facturation :
// `billingAddressRequired === false` (canal Airbnb par défaut) masque le champ
// billing_address et ne gate pas la soumission. Fail-safe identique : clé
// absente/undefined ⇒ le champ RESTE (jamais moins-disant par défaut).
export function shouldRenderField(field, values, idDocRequired, billingAddressRequired) {
  if (idDocRequired === false && PIECE_GROUP_REFS.has(field.reference)) return false
  if (billingAddressRequired === false && field.reference === BILLING_ADDRESS_REF) return false
  if (PASSPORT_REFS.has(field.reference)) return values[ID_TYPE_REF] === ID_TYPE_PASSPORT
  if (CI_REFS.has(field.reference)) return values[ID_TYPE_REF] === ID_TYPE_CI
  if (TS_REFS.has(field.reference)) return values[ID_TYPE_REF] === ID_TYPE_TS
  return true
}

// ── GuestFormView ─────────────────────────────────────────────────────────
// Rendu de l'étape « form » (hero + card + champs + submit). Props :
//   formConfig     { title, description, redirect_url, cover_photo_url, cover_photo_b64, fields[] }
//   logementName   string
//   reservation    { start_date, end_date } | null
//   schedule       { checkin_start, checkin_end, checkout_time }
//   idDocRequired  bool
//   billingAddressRequired  bool — false = adresse de facturation masquée
//                  (décision 2026-09-06, canal Airbnb) ; absent = champ conservé
//   values / uploads   états contrôlés par le conteneur
//   lang / step        'fr'|'en'|'es'  /  'form'|'submitting'
//   error          string | null
//   onSetValue(ref, v) · onChangeLang(loc) · onSubmit(e) · onPickIdFile(ref, file)
//   previewMode    bool — affiche un bandeau « soumission désactivée » (admin).
export default function GuestFormView({
  formConfig, logementName, reservation, schedule, idDocRequired,
  billingAddressRequired,
  values, uploads, lang, step, error,
  onSetValue, onChangeLang, onSubmit, onPickIdFile,
  previewMode = false,
}) {
  const setValue = onSetValue
  const changeLang = onChangeLang
  const handleSubmit = onSubmit
  const startIdUpload = onPickIdFile

  // Adresse (stockage) d'abord, base64 en compatibilité — jamais les deux.
  const coverPhoto = resolveCoverPhoto(formConfig)
  const hasCover = !!coverPhoto
  // ADR-075 fix : padding-bottom hero augmenté + chevauchement card réduit
  // pour que le sous-titre "Préparons ensemble votre arrivée" reste visible.
  const heroHeight = hasCover ? 'min(54vh, 420px)' : 'min(44vh, 360px)'
  return (
    <div style={pageStyle}>
      <FbzGlobalStyles />
      {/* ═════ Hero immersif (photo OU gradient navy→sky fallback) ═════ */}
      <div data-cover-src={coverPhoto || ''} style={{
        position:'relative', width:'100%', height: heroHeight,
        background: hasCover
          ? `linear-gradient(180deg, rgba(33, 83, 135, 0.35) 0%, rgba(33, 83, 135, 0.55) 60%, rgba(33, 83, 135, 0.85) 100%), url(${coverPhoto}) center / cover no-repeat`
          : `linear-gradient(135deg, ${DS.navy} 0%, ${DS.navyLight} 50%, ${DS.sky} 100%)`,
        overflow:'hidden',
      }}>
        {/* Subtle decorative noise/grain via SVG pattern (gradient mesh feel) */}
        {!hasCover && (
          <div aria-hidden style={{
            position:'absolute', inset:0,
            backgroundImage: `radial-gradient(circle at 20% 30%, rgba(255,255,255,.12) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(193, 123, 92, .15) 0%, transparent 50%)`,
            pointerEvents:'none',
          }}/>
        )}
        <div style={{
          position:'absolute', bottom:0, left:0, right:0, padding:'24px 24px 64px',
          textAlign:'center', color: DS.white,
          animation:'fbzFadeUp .8s cubic-bezier(.22,.61,.36,1) both',
        }}>
          <p style={{
            fontFamily:DS.fontBody, fontSize:11, letterSpacing:'.32em', textTransform:'uppercase',
            opacity:.85, marginBottom:8, fontWeight:600,
          }}>
            Fullbnbzen — Conciergerie
          </p>
          <h1 style={{
            fontFamily:DS.fontDisplay, fontStyle:'italic', fontWeight:700,
            fontSize:'clamp(28px, 5vw, 42px)', lineHeight:1.15, letterSpacing:'-0.015em',
            color:DS.white, marginBottom:8, textShadow:'0 2px 18px rgba(0,0,0,.25)',
          }}>
            {logementName}
          </h1>
          <p style={{
            fontFamily:DS.fontBody, fontSize:14, opacity:.95, fontWeight:300,
            maxWidth:520, margin:'0 auto', lineHeight:1.6,
          }}>
            {t(lang, 'hero_subtitle')}
          </p>
        </div>
      </div>

      {/* ═════ Card formulaire (chevauche le hero, négative top margin) ═════ */}
      <div style={{
        maxWidth:640, margin:'-20px auto 0', padding:'0 16px 24px',
        position:'relative', zIndex:2,
      }}>
        <div style={{
          background:DS.white, borderRadius:18,
          boxShadow:'0 18px 50px rgba(33, 83, 135, .12), 0 4px 12px rgba(33, 83, 135, .04)',
          overflow:'hidden',
          animation:'fbzFadeUp .8s cubic-bezier(.22,.61,.36,1) .15s both',
        }}>
          <div style={{padding:'36px clamp(20px, 4vw, 40px) 32px'}}>
            {/* Bandeau APERÇU (admin uniquement) — le rendu ci-dessous est le VRAI
                composant voyageur ; la soumission y est neutralisée. */}
            {previewMode && (
              <div style={{
                display:'flex', alignItems:'center', gap:8, marginBottom:16,
                padding:'8px 12px', borderRadius:10,
                background:`${DS.skyPastel}26`, border:`1px dashed ${DS.sky}`,
                fontFamily:DS.fontBody, fontSize:11.5, fontWeight:700, color:DS.navy,
              }}>
                <span aria-hidden="true">👁️</span>
                <span>
                  Aperçu voyageur (même rendu que le vrai formulaire) — soumission
                  désactivée. En EN/ES, les libellés personnalisés restent en
                  français ici (traduits automatiquement côté voyageur) ; les
                  créneaux horaires réels s'appliquent côté voyageur.
                </span>
              </div>
            )}
            {/* LOT B5 — sélecteur de langue FR/EN/ES (état local + URL ?lang=). */}
            <div style={{display:'flex', justifyContent:'flex-end', gap:6, marginBottom:16}}>
              {FORM_LOCALES.map((loc) => (
                <button key={loc} type="button" onClick={() => changeLang(loc)}
                  aria-pressed={lang === loc}
                  style={{
                    padding:'5px 10px', borderRadius:8, cursor:'pointer',
                    fontFamily:DS.fontBody, fontSize:12, fontWeight:700,
                    border:`1px solid ${lang === loc ? DS.navy : DS.border}`,
                    background: lang === loc ? DS.navy : DS.white,
                    color: lang === loc ? DS.white : DS.navyDark,
                    transition:'all .15s ease',
                  }}>
                  {FORM_LOCALE_LABELS[loc]}
                </button>
              ))}
            </div>
            <h2 style={{
              fontFamily:DS.fontDisplay, fontStyle:'italic', fontSize:24, fontWeight:700,
              color:DS.navy, marginBottom:8, letterSpacing:'-0.01em', lineHeight:1.25,
            }}>
              {formConfig.title}
            </h2>

            {reservation?.start_date && reservation?.end_date && (
              <div style={{
                display:'flex', alignItems:'center', gap:12, padding:'14px 16px',
                background:`linear-gradient(135deg, ${DS.skyPastel}33 0%, ${DS.skyPastel}66 100%)`,
                border:`1px solid ${DS.skyPastel}`, borderRadius:12,
                marginTop:14, marginBottom:18,
              }}>
                <span style={{fontSize:20, lineHeight:1}}>📅</span>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{
                    fontFamily:DS.fontBody, fontSize:10, letterSpacing:'.18em', textTransform:'uppercase',
                    color:DS.navy, fontWeight:700, marginBottom:2,
                  }}>
                    {t(lang, 'stay_label')}
                  </div>
                  <div style={{fontFamily:DS.fontBody, fontSize:14, color:DS.navyDark, fontWeight:600}}>
                    {t(lang, 'stay_dates', { d1: formatDateFR(reservation.start_date), d2: formatDateFR(reservation.end_date) })}
                  </div>
                </div>
              </div>
            )}

            {formConfig.description && (
              <p style={{
                // Charte voyageur (ADR-149) : corps Raleway navy, aéré. Les sauts
                // de ligne saisis par l'admin sont PRÉSERVÉS (white-space:pre-line —
                // conserve les retours à la ligne, dont la signature « Néïsia &
                // Dimitri - Fullbnbzen » posée sur sa propre ligne — tout en
                // normalisant les espaces parasites). Contenu inchangé : style seul.
                fontFamily:DS.fontBody, fontSize:14, color:DS.navyDark, lineHeight:1.75,
                marginBottom:24, whiteSpace:'pre-line', letterSpacing:'0.005em',
              }}>
                {formConfig.description}
              </p>
            )}

            {error && (
              <div role="alert" style={{
                padding:'14px 16px', background:'#fdeded', color:DS.red,
                border:`1px solid ${DS.red}33`, borderRadius:10,
                fontSize:13, marginBottom:18, fontFamily:DS.fontBody, lineHeight:1.5,
              }}>
                ⚠️ {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {(() => {
                // Repère le 1er champ du groupe pièce d'identité pour rendre le
                // titre de groupe au-dessus (point 6).
                const firstPieceIdx = idDocRequired
                  ? formConfig.fields.findIndex(f => PIECE_GROUP_REFS.has(f.reference))
                  : -1
                return formConfig.fields.map((f, idx) => {
                  if (!shouldRenderField(f, values, idDocRequired, billingAddressRequired)) return null
                  const groupTitle = idx === firstPieceIdx
                    ? (
                      <div key={`grp-${f.id}`}>
                        <GroupTitle>{t(lang, 'piece_group_title')}</GroupTitle>
                        {/* C4 — mention RGPD repliable sous le titre du module pièce.
                            Rendue UNIQUEMENT quand le module est demandé
                            (firstPieceIdx > -1 ⇒ idDocRequired). Texte i18n (lang). */}
                        <RgpdPieceNotice lang={lang} />
                      </div>
                    )
                    : null
                  return (
                    <div key={f.id}>
                      {groupTitle}
                      <FieldRender
                        field={f}
                        value={values[f.reference]}
                        onChange={v => setValue(f.reference, v)}
                        schedule={schedule}
                        lang={lang}
                        subtext={f.reference && PIECE_SUBTEXT_KEYS[f.reference] ? t(lang, PIECE_SUBTEXT_KEYS[f.reference]) : undefined}
                        uploadState={uploads[f.reference]}
                        onPickIdFile={file => startIdUpload(f.reference, file)}
                      />
                    </div>
                  )
                })
              })()}
              <button type="submit" disabled={step === "submitting"}
                style={{
                  width:'100%', marginTop:24,
                  background: step === "submitting" ? '#a7a39c' : DS.navy,
                  color:DS.white, border:'none', borderRadius:12,
                  padding:'16px 24px', fontSize:14, fontWeight:700,
                  letterSpacing:'.12em', textTransform:'uppercase',
                  cursor: step === "submitting" ? 'not-allowed' : 'pointer',
                  fontFamily:DS.fontBody,
                  boxShadow:'0 8px 20px rgba(33, 83, 135, .28)',
                  transition:'transform .2s ease, box-shadow .2s ease, background .2s ease',
                }}
                onMouseEnter={e => { if (step !== 'submitting') { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(33, 83, 135, .36)' } }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(33, 83, 135, .28)' }}>
                {step === "submitting" ? t(lang, 'submitting') : t(lang, 'submit')}
              </button>
            </form>
          </div>
        </div>

        {/* Footer minimaliste */}
        <p style={{
          textAlign:'center', fontSize:10, color:DS.textLight, marginTop:24,
          fontFamily:DS.fontBody, letterSpacing:'.18em', textTransform:'uppercase',
        }}>
          Fullbnbzen <span style={{margin:'0 8px', opacity:.5}}>·</span> Conciergerie locative
        </p>
      </div>
    </div>
  )
}

// ADR-075 — keyframes globaux + focus rings via <style> tag inline.
// Inline styles React ne supportent pas les pseudos (:focus, :hover) ni
// les animations keyframes — on injecte donc un <style> au top-level.
export function FbzGlobalStyles() {
  return (
    <style>{`
      @keyframes fbzFadeUp {
        0% { opacity: 0; transform: translateY(24px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      .fbz-form-input:focus, .fbz-form-input:focus-visible {
        outline: none;
        border-color: ${DS.sky};
        box-shadow: 0 0 0 4px ${DS.skyPastel}66;
      }
      .fbz-form-input::placeholder {
        color: ${DS.textLight};
        opacity: 0.7;
      }
      @media (max-width: 540px) {
        .fbz-form-input { font-size: 16px !important; } /* iOS zoom prevention */
      }
    `}</style>
  )
}

// C4 — mention RGPD pièce d'identité (texte validé Dimitri 2026-06-13 :
// finalité + stockage privé + conservation 30 jours + droits d'accès/suppression).
// REPLIABLE (décision Dimitri) : au moment de la collecte (sous le module pièce,
// PAS en pied de page), une ligne discrète et rassurante « 🔒 Vos données sont
// protégées — en savoir plus » ; au clic, déplie le texte complet (aucune mention
// obligatoire perdue). Affiché uniquement quand le module pièce est demandé pour
// ce voyageur (RgpdPieceNotice rendu sous le GroupTitle, gated firstPieceIdx ⇒
// idDocRequired). Défaut FERMÉ : le juridique ne fait pas peur, mais reste
// accessible en un clic au bon endroit.
function RgpdPieceNotice({ lang }) {
  const [open, setOpen] = useState(false)
  // Texte RGPD = SOURCE UNIQUE dans form-i18n.js (cohérence C4 ⇄ B5). Le corps
  // contient l'email ; on le scinde pour rendre un lien mailto cliquable.
  const body = t(lang, 'rgpd_body')
  const [before, after] = body.split(FORM_CONTACT_EMAIL)
  return (
    <div style={{ marginBottom:18 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          display:'flex', alignItems:'center', gap:8, width:'100%',
          padding:'10px 14px', textAlign:'left', cursor:'pointer',
          background:`${DS.skyPastel}26`, border:`1px solid ${DS.skyPastel}`,
          borderRadius: open ? '12px 12px 0 0' : 12,
          fontFamily:DS.fontBody, fontSize:12, fontWeight:700, color:DS.navy,
        }}
      >
        <span aria-hidden="true">🔒</span>
        <span style={{flex:1}}>{t(lang, 'rgpd_toggle')}</span>
        <span aria-hidden="true" style={{ fontSize:11, color:DS.sky, fontWeight:600 }}>
          {open ? t(lang, 'rgpd_less') : t(lang, 'rgpd_more')}
        </span>
      </button>
      {open && (
        <div style={{
          padding:'12px 14px',
          background:`${DS.skyPastel}1a`, border:`1px solid ${DS.skyPastel}`, borderTop:'none',
          borderRadius:'0 0 12px 12px',
        }}>
          <p style={{ margin:0, fontFamily:DS.fontBody, fontSize:12, lineHeight:1.6, color:DS.navyDark }}>
            {before}
            <a href={`mailto:${FORM_CONTACT_EMAIL}`} style={{color:DS.navy, fontWeight:700}}>{FORM_CONTACT_EMAIL}</a>
            {after}
          </p>
        </div>
      )}
    </div>
  )
}

// Titre de groupe (point 6) — sépare visuellement la section pièce d'identité.
function GroupTitle({ children }) {
  return (
    <div style={{
      marginTop:8, marginBottom:14, paddingTop:18,
      borderTop:`1px solid ${DS.border}`,
    }}>
      <h3 style={{
        fontFamily:DS.fontDisplay, fontStyle:'italic', fontWeight:700,
        fontSize:17, color:DS.navy, margin:0, lineHeight:1.3,
      }}>
        {children}
      </h3>
    </div>
  )
}

// Hauteur de départ COMPACTE des textareas (~2 lignes) — l'auto-grow prend le
// relais dès que le contenu dépasse.
const TEXTAREA_MIN_HEIGHT = 52

// ── AutoGrowTextarea ──────────────────────────────────────────────────────
// Textarea qui démarre compacte (rows=2 / minHeight) et suit la hauteur du
// contenu. Technique JS retenue (plutôt que `field-sizing:content` CSS, encore
// non supporté par Safari/Firefox stables au moment de l'écriture) : robuste et
// cross-browser. Le resize est piloté par `useLayoutEffect([value])` — il couvre
// À LA FOIS la frappe (value contrôlée par le conteneur) ET le montage sur une
// valeur PRÉ-REMPLIE (édition / retour formulaire), pas seulement à la frappe.
// `onInput` en secours donne un ajustement immédiat même hors cycle React.
function AutoGrowTextarea({ value, onChange, required, placeholder, style, minHeight = TEXTAREA_MIN_HEIGHT }) {
  const ref = useRef(null)
  const resize = useCallback(() => {
    const el = ref.current
    if (!el) return
    // 'auto' d'abord pour que scrollHeight reflète le contenu réel (permet aussi
    // de RÉTRÉCIR quand le voyageur efface du texte), puis on borne au plancher.
    el.style.height = 'auto'
    el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`
  }, [minHeight])
  // Recalcule à chaque changement de valeur ET au montage (valeur pré-remplie).
  useLayoutEffect(() => { resize() }, [value, resize])
  return (
    <textarea
      ref={ref}
      className="fbz-form-input"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      onInput={resize}
      required={required}
      rows={2}
      placeholder={placeholder}
      style={style}
    />
  )
}

function FieldRender({ field, value, onChange, schedule, subtext, uploadState, onPickIdFile, lang }) {
  // ADR-075 — design system FBZ : inputs minimalistes avec focus ring sky,
  // labels Raleway uppercase letter-spacing, transition douces.
  const baseInputStyle = {
    width:"100%", padding:"12px 14px",
    border:`1.5px solid ${DS.border}`, borderRadius:10,
    fontSize:14, outline:"none", background:DS.white,
    fontFamily:DS.fontBody, color:DS.navyDark,
    transition:'border-color .2s ease, box-shadow .2s ease',
  }
  const labelStyle = {
    display:"block", fontSize:11, fontWeight:700, color:DS.navy,
    marginBottom:7, fontFamily:DS.fontBody,
    letterSpacing:'.12em', textTransform:'uppercase',
  }
  const helperStyle = {
    display:"block", fontSize:11, color:DS.textLight, marginTop:6,
    fontStyle:"italic", fontFamily:DS.fontBody,
  }
  const wrapStyle = { marginBottom:20 }

  // Parse options (multilignes string → array)
  const options = typeof field.options === 'string'
    ? field.options.split('\n').map(s => s.trim()).filter(Boolean)
    : (Array.isArray(field.options) ? field.options : [])

  // ADR-072 — substitution variables dans placeholder textarea (early/late requests).
  const placeholder = field.placeholder
    ? field.placeholder
        .replace(/\{checkin_start\}/g, schedule?.checkin_start || '')
        .replace(/\{checkin_end\}/g, schedule?.checkin_end || '')
        .replace(/\{checkout_time\}/g, schedule?.checkout_time || '')
    : ''

  // Helper text sous les champs time. Le bornage effectif est désormais porté
  // par TimeSlotPicker (créneaux 30 min dans [checkin..checkout]) — cf. point 4c.
  let helperText = null
  // Le champ libre « Commentaires » (reference 'comments') a été retiré du
  // formulaire de bienvenue (décision Dimitri, #1040) : plus de helper « demande
  // particulière (arrivée anticipée / départ tardif) », qui laissait attendre une
  // gratuité sur de futurs services payants. Aucun helper spécifique n'est donc
  // rendu pour ce reference (un champ 'comments' résiduel d'une config admin
  // custom reste affiché en textarea générique, sans incitation).
  if (field.type === 'time' && schedule) {
    if (field.reference === 'time_in' && schedule.checkin_start) {
      helperText = t(lang, 'time_from', { h: schedule.checkin_start })
    } else if (field.reference === 'time_out' && schedule.checkout_time) {
      helperText = t(lang, 'checkout_before', { h: schedule.checkout_time })
    }
  }

  // Libellé d'affichage d'une option : id_type → traduction du libellé (valeur
  // FR canonique conservée pour l'affichage conditionnel) ; autres → tel quel (v1).
  const optLabel = (opt) => (field.reference === ID_TYPE_REF ? pieceOptionLabel(lang, opt) : opt)

  const optionLabelStyle = {
    display:"flex", alignItems:"center", gap:10, fontSize:14, color:DS.navyDark,
    marginBottom:10, cursor:"pointer", fontFamily:DS.fontBody, lineHeight:1.4,
    padding:'10px 14px', borderRadius:10, border:`1.5px solid ${DS.border}`,
    background:DS.white, transition:'border-color .15s ease, background .15s ease',
  }

  let control = null
  if (field.type === 'textarea') {
    // Point UX — les cases commentaires démarrent COMPACTES (2 lignes) et
    // grandissent au fil de la frappe (auto-grow JS, cf. AutoGrowTextarea).
    control = (
      <AutoGrowTextarea
        value={value}
        onChange={onChange}
        required={field.required}
        placeholder={placeholder}
        minHeight={TEXTAREA_MIN_HEIGHT}
        style={{ ...baseInputStyle, resize: 'none', minHeight: TEXTAREA_MIN_HEIGHT, overflow: 'hidden' }}
      />
    )
  } else if (field.type === 'dropdown') {
    control = (
      <select className="fbz-form-input" value={value || ""} onChange={e => onChange(e.target.value)} required={field.required}
        style={{...baseInputStyle, cursor:'pointer', appearance:'none', backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23215387' stroke-width='1.8' stroke-linecap='round'/%3E%3C/svg%3E")`, backgroundRepeat:'no-repeat', backgroundPosition:'right 16px center', paddingRight:40}}>
        <option value="">{t(lang, 'select_placeholder')}</option>
        {options.map(opt => <option key={opt} value={opt}>{optLabel(opt)}</option>)}
      </select>
    )
  } else if (field.type === 'radio') {
    control = (
      <div>
        {options.map(opt => (
          <label key={opt} style={{
            ...optionLabelStyle,
            borderColor: value === opt ? DS.sky : DS.border,
            background: value === opt ? `${DS.skyPastel}33` : DS.white,
          }}>
            <input type="radio" name={field.reference} value={opt} checked={value === opt}
              onChange={() => onChange(opt)} required={field.required} style={{accentColor:DS.navy, width:18, height:18, cursor:'pointer'}}/>
            <span>{optLabel(opt)}</span>
          </label>
        ))}
      </div>
    )
  } else if (field.type === 'checkbox') {
    const arr = Array.isArray(value) ? value : []
    const toggle = (opt) => {
      const next = arr.includes(opt) ? arr.filter(x => x !== opt) : [...arr, opt]
      onChange(next)
    }
    control = (
      <div>
        {options.map(opt => (
          <label key={opt} style={{
            ...optionLabelStyle,
            borderColor: arr.includes(opt) ? DS.sky : DS.border,
            background: arr.includes(opt) ? `${DS.skyPastel}33` : DS.white,
          }}>
            <input type="checkbox" checked={arr.includes(opt)} onChange={() => toggle(opt)}
              style={{accentColor:DS.navy, width:18, height:18, cursor:'pointer'}}/>
            <span>{optLabel(opt)}</span>
          </label>
        ))}
      </div>
    )
  } else if (field.type === 'date') {
    control = <input className="fbz-form-input" type="date" value={value || ""} onChange={e => onChange(e.target.value)} required={field.required} style={baseInputStyle}/>
  } else if (field.type === 'time') {
    // Décision produit 2026-06-12 : créneaux bornés SEULS, aucune échappatoire
    // d'horaire dans le formulaire (early check-in / late check-out deviendront
    // des SERVICES PAYANTS réservables depuis le livret — pas de canal gratuit
    // ici). Les demandes particulières passent par le champ Commentaires.
    let lo = '00:00'
    let hi = '23:30'
    if (field.reference === 'time_in' && schedule?.checkin_start) lo = schedule.checkin_start
    if (field.reference === 'time_out' && schedule?.checkout_time) hi = schedule.checkout_time
    control = (
      <TimeSlotPicker value={value || ""} onChange={onChange} required={field.required} lo={lo} hi={hi} lang={lang} />
    )
  } else if (field.type === 'file') {
    // N2-IA LOT 1 (ADR-189) — upload sécurisé asynchrone (compression canvas +
    // signed URL + confirm serveur). L'état est porté par uploadState ; le submit
    // est bloqué tant que les uploads obligatoires ne sont pas 'done'.
    control = (
      <IdUploadControl
        uploadState={uploadState}
        onPickFile={onPickIdFile}
        baseInputStyle={baseInputStyle}
        lang={lang}
      />
    )
  } else {
    // `lectureSeule` / `placeholder` : posés par champsEnModification (fiche
    // déjà reçue — le courriel ne se change pas par le formulaire).
    control = <input className="fbz-form-input" type={field.input_type || "text"} value={value || ""}
      onChange={e => onChange(e.target.value)} required={field.required} style={baseInputStyle}
      readOnly={field.lectureSeule === true || undefined} placeholder={placeholder || undefined}/>
  }

  return (
    <div style={wrapStyle}>
      <label style={labelStyle}>{field.label}{field.required && <span style={{color:DS.red,marginLeft:3}}>*</span>}</label>
      {subtext && <span style={{...helperStyle, marginTop:0, marginBottom:6, fontStyle:'normal'}}>{subtext}</span>}
      {control}
      {helperText && <span style={helperStyle}>{helperText}</span>}
      {field.aide && <span data-testid={`aide-${field.reference}`} style={{...helperStyle, fontStyle:'normal'}}>{field.aide}</span>}
    </div>
  )
}

// ── IdUploadControl ───────────────────────────────────────────────────────
// N2-IA LOT 1 (ADR-189) — champ d'upload pièce d'identité avec état par fichier.
// L'upload démarre dès la sélection (asynchrone, non bloquant). États :
//   uploading → « Envoi… »  |  done → « ✓ Envoyé »  |  failed → erreur + Réessayer.
function IdUploadControl({ uploadState, onPickFile, baseInputStyle, lang }) {
  const status = uploadState?.status
  const fileInputStyle = { ...baseInputStyle, padding: '10px 14px', cursor: 'pointer' }

  return (
    <div>
      <input
        className="fbz-form-input"
        type="file"
        accept="image/*,application/pdf"
        onChange={e => onPickFile(e.target.files?.[0] || null)}
        style={fileInputStyle}
      />
      {status === 'uploading' && (
        <span style={{ display: 'block', marginTop: 8, fontSize: 12, color: DS.navy, fontFamily: DS.fontBody }}>
          {t(lang, 'upload_sending')}
        </span>
      )}
      {status === 'done' && (
        <span style={{ display: 'block', marginTop: 8, fontSize: 12, color: DS.green, fontWeight: 700, fontFamily: DS.fontBody }}>
          {/* E1a — `restored` = chemin 409-succès (le serveur a GARDÉ l'ancien
              document) : libellé générique, jamais le nom du fichier
              fraîchement choisi (il n'est jamais parti — la ligne mentirait).
              Sans `restored`, rendu byte-identique à avant. */}
          {uploadState.restored
            ? t(lang, 'upload_already_received')
            : `${t(lang, 'upload_done')}${uploadState.name ? ` (${uploadState.name})` : ''}`}
        </span>
      )}
      {status === 'failed' && (
        <div style={{ marginTop: 8 }}>
          <span style={{ display: 'block', fontSize: 12, color: DS.red, fontFamily: DS.fontBody }}>
            ⚠️ {uploadState.error || t(lang, 'upload_err_generic')}
          </span>
        </div>
      )}
      <span style={{ display: 'block', marginTop: 6, fontSize: 11, color: DS.textLight, fontStyle: 'italic', fontFamily: DS.fontBody }}>
        {t(lang, 'upload_replace_hint', { action: status === 'failed' ? t(lang, 'upload_action_retry') : t(lang, 'upload_action_replace') })}
      </span>
    </div>
  )
}

// TimeSlotPicker (point 4c) — select unique de créneaux 30 min bornés à
// [lo .. hi] (inclus). Plus simple et plus sûr que 2 selects HH/MM libres :
// l'utilisateur ne PEUT PAS choisir hors bornes. Submit en string HH:MM.
function buildSlots(lo, hi) {
  const toMin = (s) => { const m = (s || '').match(/^(\d{1,2}):(\d{2})$/); return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null }
  const loM = toMin(lo) ?? 0
  const hiM = toMin(hi) ?? 23 * 60 + 30
  const out = []
  for (let m = Math.ceil(loM / 30) * 30; m <= hiM; m += 30) {
    out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`)
  }
  // Garantit au moins le créneau de départ exact si la grille l'a sauté.
  if (out.length === 0 && loM <= hiM) out.push(`${String(Math.floor(loM / 60)).padStart(2, '0')}:${String(loM % 60).padStart(2, '0')}`)
  return out
}

function TimeSlotPicker({ value, onChange, required, lo, hi, lang }) {
  const slots = buildSlots(lo, hi)
  const selectStyle = {
    width:'100%', padding:'12px 14px',
    border:`1.5px solid ${DS.border}`, borderRadius:10,
    fontSize:14, outline:'none', background:DS.white,
    fontFamily:DS.fontBody, color:DS.navyDark, cursor:'pointer', appearance:'none',
    backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23215387' stroke-width='1.8' stroke-linecap='round'/%3E%3C/svg%3E")`,
    backgroundRepeat:'no-repeat', backgroundPosition:'right 16px center', paddingRight:40,
    transition:'border-color .2s ease, box-shadow .2s ease',
  }
  return (
    <select className="fbz-form-input" value={value || ''} onChange={e => onChange(e.target.value)} required={required} style={selectStyle} aria-label="Créneau horaire">
      <option value="">{t(lang, 'slot_placeholder')}</option>
      {slots.map(s => <option key={s} value={s}>{s}</option>)}
    </select>
  )
}

export function substituteVars(s, log, schedule = {}, reservation = null) {
  if (!s) return s
  return s
    .replace(/\{logement_name\}/g, log.nom || log.id || '')
    .replace(/\{logement_full_name\}/g, log.nom_complet || log.nom || log.id || '')
    .replace(/\{checkin_start\}/g, schedule.checkin_start || '')
    .replace(/\{checkin_end\}/g, schedule.checkin_end || '')
    .replace(/\{checkout_time\}/g, schedule.checkout_time || '')
    .replace(/\{arrival_date\}/g, reservation?.start_date ? formatDateFR(reservation.start_date) : '')
    .replace(/\{departure_date\}/g, reservation?.end_date ? formatDateFR(reservation.end_date) : '')
}

export function formatDateFR(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

// ADR-075 — refonte design page publique (Editorial Conciergerie).
// Direction : luxe maîtrisé. Playfair Display pour titres, Raleway body.
// Hero immersif (cover photo + overlay navy OU gradient navy→sky fallback).
// Cards refined avec ombre douce, focus rings sky, CTA terracotta.
// Mobile-first : hero compact, padding réduit, submit pleine largeur.

const pageStyle = {
  minHeight: '100vh',
  background: DS.white,
  fontFamily: DS.fontBody,
  color: DS.navyDark,
}

// ═══ Formulaire DÉJÀ ENVOYÉ (défaut voyageur du 01/10, décisions du 03/10) ══
// Rendu quand /api/form-config porte `deja_recu` : « Bien reçu le … » + les
// réponses en TEXTE, déjà MASQUÉES par le serveur. Tant que le voyageur n'est
// pas arrivé (`deja.modifiable`, décidé par le serveur), un bouton « Modifier
// mes réponses » rouvre LE MÊME formulaire (mode modification : voir
// champsEnModification) ; après, lecture seule + « écrivez-nous ». Rien de la
// pièce d'identité n'est affiché (ni type, ni image) : une seule ligne
// « Pièce d'identité reçue ». Même charte que l'écran merci (Centered).
const RECU_LOCALE = { fr: 'fr-FR', en: 'en-GB', es: 'es-ES' }
export function formatDateRecu(lang, iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  try {
    return d.toLocaleDateString(RECU_LOCALE[lang] || 'fr-FR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Paris' })
  } catch (_) {
    return formatDateFR(iso)
  }
}
// Le serveur envoie chaque réponse DÉJÀ masquée : { valeur } (heure, choix
// fermé, e-mail/téléphone réduits) ou { recu: 'adresse'|'message'|'reponse' }
// (contenu jamais transmis). Toute autre forme est ignorée.
const RECU_KINDS = { adresse: 'recu_val_adresse', message: 'recu_val_message', reponse: 'recu_val_reponse' }
function valeurRecue(lang, field, r) {
  if (!r || typeof r !== 'object') return null
  if (typeof r.recu === 'string') return RECU_KINDS[r.recu] ? t(lang, RECU_KINDS[r.recu]) : null
  if (typeof r.valeur !== 'string' || !r.valeur) return null
  if (r.valeur === 'apres_23h' && field && field.reference === 'time_in') return t(lang, 'v2_apres_23h')
  return r.valeur
}
// Limite de modification : formatLimiteModification (src/lib/form-i18n.js).

// ── Mode MODIFICATION (décision Dimitri 03/10) ───────────────────────────────
// Le serveur ne renvoie en clair que les heures et les choix fermés : ce sont
// les seules réponses PRÉ-REMPLIES. Les autres réponses déjà reçues (e-mail,
// téléphone, adresse, messages, texte libre) restent vides, deviennent
// facultatives et portent « Déjà reçu — laissez vide pour garder » : le
// serveur garde l'ancienne valeur quand le champ revient vide
// (mergeForUpdate). La pièce d'identité déjà reçue n'est pas redemandée.
const CHOIX_SIMPLES = new Set(['dropdown', 'select', 'radio'])
export function valeursModification(fields, deja) {
  const reponses = (deja && deja.reponses) || {}
  const out = {}
  for (const f of Array.isArray(fields) ? fields : []) {
    if (!f || !f.reference) continue
    const r = reponses[f.reference]
    if (!r || typeof r !== 'object') continue
    if (f.type === 'time' && typeof r.valeur === 'string' && r.valeur) out[f.reference] = r.valeur
    else if (Array.isArray(r.choix) && r.choix.length) {
      if (f.type === 'checkbox') out[f.reference] = r.choix.slice()
      else if (CHOIX_SIMPLES.has(f.type)) out[f.reference] = r.choix[0]
    }
  }
  return out
}
// Courriel déjà reçu (relecture 03/10, point 3) : il ne se change PAS par le
// formulaire — lecture seule, l'adresse masquée en indication, et dessous
// « Pour changer votre adresse e-mail, écrivez-nous. ». Le serveur l'ignore
// de toute façon (mergeForUpdate) et ne touche pas le courriel de la résa.
export function champsEnModification(fields, deja, lang) {
  const reponses = (deja && deja.reponses) || {}
  const prerempli = valeursModification(fields, deja)
  return (Array.isArray(fields) ? fields : []).map((f) => {
    if (!f || !f.reference) return f
    const r = reponses[f.reference]
    const dejaRecu = r && typeof r === 'object'
    if (!dejaRecu || Object.prototype.hasOwnProperty.call(prerempli, f.reference)) return f
    if (f.reference === 'email') {
      return {
        ...f,
        required: false,
        lectureSeule: true,
        placeholder: typeof r.valeur === 'string' && r.valeur ? r.valeur : t(lang, 'recu_val_reponse'),
        aide: t(lang, 'recu_email_aide'),
      }
    }
    return { ...f, required: false, placeholder: t(lang, 'recu_garder') }
  })
}

export function GuestFormRecu({ lang, onChangeLang = null, fields = [], deja = null, journeyUrl = null, onModifier = null, avisRefus = null }) {
  const reponses = (deja && deja.reponses) || {}
  const date = formatDateRecu(lang, deja && deja.submitted_at)
  // Vraie limite servie par le serveur (relectures du 28/10) : « jusqu'au
  // 14 juillet à 17 h (heure de Paris) », ou « jusqu'à la veille de votre
  // arrivée » sans heure d'arrivée réglée (limite = minuit au début du jour
  // d'arrivée). Limite passée (onglet resté ouvert) → ni phrase ni bouton.
  const texteLimite = deja && deja.modifiable
    ? texteLimiteModification(
        lang,
        // avec_heure absent = heure connue (seul false dit « sans heure »).
        { jusqu_au: deja.modifiable_jusqu_au, avec_heure: deja.modifiable_avec_heure !== false },
        { date: 'recu_modifiable', veille: 'recu_modifiable_jour' },
      )
    : null
  const modifiable = !!(texteLimite && onModifier)
  const lignes = (Array.isArray(fields) ? fields : [])
    .filter((f) => f && f.reference && f.type !== 'file' && !PIECE_GROUP_REFS.has(f.reference))
    .map((f) => ({ f, texte: valeurRecue(lang, f, reponses[f.reference]) }))
    .filter((l) => l.texte)
  return (
    <Centered>
      {onChangeLang && (
        <div style={{display:'flex', justifyContent:'center', gap:6, marginBottom:16}}>
          {FORM_LOCALES.map((loc) => (
            <button key={loc} type="button" onClick={() => onChangeLang(loc)}
              aria-pressed={lang === loc}
              style={{
                padding:'5px 10px', borderRadius:8, cursor:'pointer', minHeight:32,
                fontFamily:DS.fontBody, fontSize:12, fontWeight:700,
                border:`1px solid ${lang === loc ? DS.navy : DS.border}`,
                background: lang === loc ? DS.navy : DS.white,
                color: lang === loc ? DS.white : DS.navyDark,
              }}>
              {FORM_LOCALE_LABELS[loc]}
            </button>
          ))}
        </div>
      )}
      {avisRefus && (
        // 409 'deja_recu' : jamais de fausse confirmation — l'envoi n'est PAS
        // enregistré. Modification refusée → « trop tard » ; premier envoi
        // refusé (réponse déjà reçue par un autre lien) → le dire, avec la
        // bonne porte : « Modifier mes réponses » si possible, sinon écrire.
        <p data-testid={avisRefus === 'trop_tard' ? 'form-recu-trop-tard' : 'form-recu-autre-reponse'} role="alert"
          style={{...msgStyle, color:'#9a3412', background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:10, padding:'10px 12px', marginBottom:14}}>
          {avisRefus === 'trop_tard'
            ? t(lang, 'recu_trop_tard')
            : t(lang, modifiable ? 'recu_autre_reponse_modifiable' : 'recu_autre_reponse')}
        </p>
      )}
      <h1 data-testid="form-recu-titre" style={{...titleStyle, color:DS.green}}>
        {date ? t(lang, 'recu_titre', { date }) : t(lang, 'recu_titre_sans_date')}
      </h1>
      <p style={msgStyle}>{t(lang, 'recu_intro')}</p>
      <dl data-testid="form-recu-reponses" style={{textAlign:'left', margin:'18px 0 0', fontFamily:DS.fontBody}}>
        {lignes.map(({ f, texte }) => (
          <div key={f.reference} style={{padding:'10px 0', borderTop:`1px solid ${DS.border}`}}>
            <dt style={{fontSize:12, color:DS.navy, fontWeight:700, marginBottom:3}}>{f.label || f.reference}</dt>
            <dd style={{margin:0, fontSize:14, color:DS.navyDark, lineHeight:1.5, whiteSpace:'pre-wrap', overflowWrap:'anywhere'}}>
              {texte}
            </dd>
          </div>
        ))}
        {deja && deja.piece_recue && (
          <div data-testid="form-recu-piece" style={{padding:'10px 0', borderTop:`1px solid ${DS.border}`}}>
            <dt style={{margin:0, fontSize:14, color:DS.green, fontWeight:700}}>✓ {t(lang, 'recu_piece')}</dt>
          </div>
        )}
      </dl>
      {modifiable ? (
        <div style={{marginTop:18}}>
          <button type="button" data-testid="form-recu-modifier" onClick={onModifier}
            style={{
              padding:'10px 18px', borderRadius:10, cursor:'pointer', minHeight:44,
              fontFamily:DS.fontBody, fontSize:14, fontWeight:700,
              border:`1px solid ${DS.navy}`, background:DS.white, color:DS.navy,
            }}>
            {t(lang, 'recu_btn_modifier')}
          </button>
          <p data-testid="form-recu-limite" style={{...msgStyle, fontSize:12, color:'#888', marginTop:10}}>
            {texteLimite}
          </p>
        </div>
      ) : (
        <p data-testid="form-recu-fige" style={{...msgStyle, fontSize:12, color:'#888', marginTop:16}}>{t(lang, 'recu_modif')}</p>
      )}
      {journeyUrl && (
        <p style={{marginTop:14}}>
          <a href={journeyUrl} style={{color:DS.navy, fontWeight:700, fontFamily:DS.fontBody, fontSize:14}}>
            {t(lang, 'v2_retour_sejour')}
          </a>
        </p>
      )}
    </Centered>
  )
}

export function Centered({ children }) {
  return (
    <div style={{...pageStyle, display:"flex", alignItems:"center", justifyContent:"center", padding: '24px 16px'}}>
      <div style={{
        maxWidth: 480, width:"100%", background: DS.white, borderRadius: 18,
        boxShadow: '0 12px 40px rgba(33, 83, 135, .08), 0 2px 8px rgba(33, 83, 135, .04)',
        padding: "44px 32px", textAlign:"center",
        animation: 'fbzFadeUp .6s cubic-bezier(.22,.61,.36,1) both',
      }}>{children}</div>
    </div>
  )
}
export const titleStyle = { fontFamily: DS.fontDisplay, fontSize: 28, color: DS.navy, marginBottom: 14, fontStyle:'italic', fontWeight: 700, letterSpacing: '-0.01em' }
export const msgStyle = { fontSize: 14, color: DS.navyDark, lineHeight: 1.7, fontFamily: DS.fontBody }
